import HistorialCambios from "components/HistorialCambios";
import { errorMessage } from "utils/errorMessage";
import { useContext, useState } from "react";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Tooltip,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DataTable from "components/Tables/DataTable";
import CustomPagination from "components/Pagination";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import { MessageManager } from "context";
import Formulario, { Campo, empty, camposPendientes } from "./Formulario";
import Administracion from "./Administracion";
import DatosEmpresa from "./DatosEmpresa";
import { DETAIL, LIST, OPTIONS, SAVE, LABELS, actionMutation, download } from "./api";
import { crearPDF } from "./pdf";
const states = [
  "Borrador",
  "Pendiente de aprobación",
  "Aprobado",
  "Finalizado",
  "Rechazado",
  "Anulado",
];
const actionText = (action, manifest) =>
  action.accion === "ACEPTAR" && manifest.idTransportista === manifest.idOperador
    ? "Aceptar transporte y operación"
    : `${LABELS[action.accion]} · ${action.rol}`;
const currentYear = () =>
  Number(
    new Intl.DateTimeFormat("en", {
      timeZone: "America/Argentina/Buenos_Aires",
      year: "numeric",
    }).format(new Date())
  );
export default function Manifiestos() {
  const client = useApolloClient(),
    { handleSnackbar } = useContext(MessageManager);
  const [tab, setTab] = useState(0),
    [filters, setFilters] = useState({
      page: 1,
      search: "",
      estado: "",
      anio: null,
      idEmpresa: null,
      actividad: null,
      pendientes: false,
    });
  const [id, setId] = useState(new URLSearchParams(window.location.search).get("id")),
    [form, setForm] = useState(null),
    [action, setAction] = useState(null),
    [input, setInput] = useState({}),
    [busy, setBusy] = useState(false),
    [actionError, setActionError] = useState(""),
    [uploadType, setUploadType] = useState("Contingencia"),
    [preview, setPreview] = useState(null),
    [infoOpen, setInfoOpen] = useState(false),
    [summary, setSummary] = useState(null);
  const [summaryArgs, setSummaryArgs] = useState({
    idEmpresa: "",
    actividad: "Generador",
    anio: currentYear(),
  });
  const [revisarFormulario, setRevisarFormulario] = useState(null);
  const {
    data: optionsData,
    loading: loadingOptions,
    error: optionsError,
    refetch: refreshOptions,
  } = useQuery(OPTIONS, { fetchPolicy: "network-only" });
  const {
    data: list,
    loading: loadingList,
    error: listError,
    refetch: refreshList,
  } = useQuery(LIST, { variables: filters, fetchPolicy: "network-only" });
  const {
    data: detail,
    error: detailError,
    loading: loadingDetail,
    refetch: refreshDetail,
  } = useQuery(DETAIL, { variables: { id: id || "0" }, skip: !id, fetchPolicy: "network-only" });
  const o = optionsData?.manifiestoOpciones,
    m = detail?.manifiesto,
    vehiculo = m?.snapshot?.vehiculo || m?.datos?.vehiculo;
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await refreshList();
      await refreshOptions();
      if (id) await refreshDetail();
      return true;
    } catch (e) {
      handleSnackbar(e.message, "error");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const save = (f, archivos = [], presentar = false) =>
    run(async () => {
      const result = await client.mutate({ mutation: SAVE, variables: { input: f } });
      const savedId = result.data.guardarManifiestoBorrador.id;
      f.id = savedId;
      while (archivos.length) {
        const { tipo, archivo } = archivos[0];
        await client.mutate({
          mutation: gql`
            mutation ($id: ID!, $tipo: String!, $archivo: Upload!) {
              adjuntarManifiesto(id: $id, tipo: $tipo, archivo: $archivo) {
                id
              }
            }
          `,
          variables: { id: savedId, tipo, archivo },
        });
        archivos.shift();
      }
      if (presentar)
        await client.mutate({
          mutation: actionMutation("PRESENTAR"),
          variables: { id: savedId, input: {} },
        });
      setId(savedId);
      setForm(null);
      setRevisarFormulario(null);
      handleSnackbar(presentar ? "Manifiesto presentado" : "Borrador guardado", "success");
    });
  const filter = (k, v) => setFilters({ ...filters, [k]: v, page: 1 });
  const company = (id) => o?.empresas.find((e) => String(e.id) === String(id))?.razonSocial || id;
  const canEdit =
    m?.estado === "Borrador" &&
    o?.responsable &&
    o.empresas.some((e) => e.propia && String(e.id) === String(m.idGenerador));
  const previewPDF = () => {
    const enriched = {
      ...m,
      snapshot: m.snapshot || {
        configuracion: optionsData.manifiestoConfiguracionVigente,
        ...Object.fromEntries(
          ["Generador", "Transportista", "Operador"].map((r) => {
            const e = o.empresas.find((e) => String(e.id) === String(m[`id${r}`]));
            const i = o.inscripciones.find(
              (i) => String(i.idEmpresa) === String(e?.id) && i.actividad === r
            );
            return [r, { ...e, ...i, legajo: String(e?.id || "") }];
          })
        ),
      },
    };
    return crearPDF(enriched);
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Stack
        spacing={3}
        sx={{
          py: 2,
          "& .MuiButton-outlined, & .MuiButton-text": { color: "primary.main", minHeight: 44 },
          "& .MuiButton-root.Mui-disabled": {
            background: "#dee2e6 !important",
            color: "text.primary !important",
            opacity: 1,
          },
        }}
      >
        <Typography variant="h4">Manifiestos</Typography>
        {(optionsError || listError || detailError) && (
          <Alert severity="error">{errorMessage(optionsError || listError || detailError)}</Alert>
        )}
        {loadingOptions ? (
          <CircularProgress />
        ) : (
          o && (
            <>
              <Tabs
                value={tab}
                onChange={(_, v) => {
                  setTab(v);
                  setForm(null);
                  setId(null);
                }}
                variant="scrollable"
                allowScrollButtonsMobile
                aria-label="Secciones de manifiestos"
                sx={{
                  bgcolor: "transparent",
                  borderRadius: 0,
                  borderBottom: 1,
                  borderColor: "divider",
                  p: 0,
                  minHeight: 48,
                  "& .MuiTabs-indicator": {
                    height: 3,
                    bottom: 0,
                    borderRadius: "3px 3px 0 0",
                    bgcolor: "primary.main",
                    boxShadow: "none",
                    transition: "none",
                  },
                  "& .MuiTab-root": {
                    flex: "none",
                    minHeight: 48,
                    px: 2.5,
                    py: 1,
                    borderRadius: 0,
                    fontWeight: 600,
                    color: "text.secondary !important",
                  },
                  "& .MuiTab-root.Mui-selected": { color: "primary.main !important" },
                  "& .MuiTab-root:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: -2,
                  },
                }}
              >
                <Tab label="Manifiestos" value={0} />
                {/* Respaldo DDJJ oculto temporalmente. */}
                {(!o.autoridad || o.puedeInscripciones || o.puedeHabilitaciones) && (
                  <Tab
                    label={o.autoridad ? "Empresas y vehículos" : "Datos de mi empresa"}
                    value={2}
                  />
                )}
                {o.admin && <Tab label="Administración" value={3} />}
              </Tabs>
              {tab === 0 && (
                <>
                  {!form && !id && (
                    <>
                      {o.responsable &&
                        o.empresas.some((e) => e.propia && e.actividad?.includes("Generador")) && (
                          <SoftButton
                            variant="gradient"
                            color="primary"
                            startIcon={<AddIcon />}
                            sx={{
                              alignSelf: "flex-start",
                              width: "fit-content",
                              textTransform: "none",
                            }}
                            onClick={() =>
                              setForm({
                                idGenerador: "",
                                idTransportista: "",
                                idOperador: "",
                                datos: empty,
                              })
                            }
                          >
                            Nuevo manifiesto
                          </SoftButton>
                        )}
                      <Card>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                              <Campo
                                label="Número"
                                value={filters.search}
                                onChange={(v) => filter("search", v)}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Campo
                                label="Estado"
                                value={filters.estado}
                                onChange={(v) => filter("estado", v)}
                                options={[
                                  { id: "", label: "Todos" },
                                  ...states.map((s) => ({ id: s, label: s })),
                                ]}
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Campo
                                label="Año"
                                type="number"
                                value={filters.anio || ""}
                                onChange={(v) => filter("anio", v ? Number(v) : null)}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Campo
                                label="Empresa"
                                autocomplete
                                value={filters.idEmpresa || ""}
                                onChange={(v) => filter("idEmpresa", v || null)}
                                options={[
                                  { id: "", label: "Todas" },
                                  ...o.empresas
                                    .filter((e) => o.autoridad || e.propia)
                                    .map((e) => ({ id: e.id, label: e.razonSocial })),
                                ]}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Campo
                                label="Actividad"
                                value={filters.actividad || ""}
                                onChange={(v) => filter("actividad", v || null)}
                                options={[
                                  { id: "", label: "Todas" },
                                  ...["Generador", "Transportista", "Operador"].map((s) => ({
                                    id: s,
                                    label: s,
                                  })),
                                ]}
                              />
                            </Grid>
                          </Grid>
                          <FormControlLabel
                            sx={{ display: "flex", width: "fit-content", ml: "auto", mr: 0 }}
                            label="Solo mis acciones pendientes"
                            control={
                              <Checkbox
                                checked={filters.pendientes}
                                onChange={(e) => filter("pendientes", e.target.checked)}
                              />
                            }
                          />
                        </CardContent>
                      </Card>
                      <Card
                        sx={{
                          "& .MuiTable-root": {
                            display: "table",
                            width: "100%",
                            minWidth: 900,
                            tableLayout: "fixed",
                          },
                          "& td": { overflowWrap: "anywhere" },
                        }}
                      >
                        {loadingList ? (
                          <CardContent>
                            <CircularProgress />
                          </CardContent>
                        ) : list?.manifiestos.items.length ? (
                          <DataTable
                            columns={[
                              ["numero", "Número", "12%"],
                              ["estado", "Estado", "12%"],
                              ["generador", "Generador", "18%"],
                              ["transportista", "Transportista", "18%"],
                              ["operador", "Operador", "18%"],
                              ["pendientes", "Acción pendiente", "16%"],
                              ["acciones", "\u00a0", "6%"],
                            ].map(([name, desc, width]) => ({
                              name,
                              desc,
                              width,
                              align: name === "acciones" ? "center" : "left",
                              noOrder: true,
                            }))}
                            rows={list.manifiestos.items.map((x) => ({
                              hasBorder: true,
                              numero: (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="dark.main"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {x.numero || "-"}
                                </Typography>
                              ),
                              estado: (
                                <Typography component="span" variant="caption" color="dark.main">
                                  {x.estado}
                                </Typography>
                              ),
                              ...Object.fromEntries(
                                ["Generador", "Transportista", "Operador"].map((rol) => [
                                  rol.toLowerCase(),
                                  <Typography
                                    component="span"
                                    key={rol}
                                    variant="caption"
                                    color="dark.main"
                                    sx={{
                                      display: "inline-block",
                                      maxWidth: 220,
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    {company(x[`id${rol}`])}
                                  </Typography>,
                                ])
                              ),
                              pendientes: (
                                <Typography component="span" variant="caption" color="dark.main">
                                  {x.acciones
                                    ?.filter((a) => !["ANULAR", "RECHAZAR"].includes(a.accion))
                                    .map((a) => actionText(a, x))
                                    .join(" · ") || "Sin acciones pendientes"}
                                </Typography>
                              ),
                              acciones: (
                                <Tooltip title="Abrir manifiesto">
                                  <IconButton
                                    aria-label={`Abrir ${x.numero || `borrador ${x.id}`}`}
                                    sx={{ color: "primary.main", width: 44, height: 44 }}
                                    onClick={() => setId(x.id)}
                                  >
                                    <ArrowForwardIcon />
                                  </IconButton>
                                </Tooltip>
                              ),
                            }))}
                          />
                        ) : (
                          <CardContent
                            sx={{
                              display: "grid",
                              placeItems: "center",
                              minHeight: 220,
                              textAlign: "center",
                            }}
                          >
                            <Typography color="dark.main">No se encontraron resultados.</Typography>
                          </CardContent>
                        )}
                        <CustomPagination
                          loading={loadingList}
                          length={list?.manifiestos.items.length || 0}
                          total={list?.manifiestos.total || 0}
                          pages={Math.max(1, Math.ceil((list?.manifiestos.total || 0) / 20))}
                          page={filters.page}
                          setPage={(page) => setFilters({ ...filters, page })}
                          noCard
                        />
                      </Card>
                    </>
                  )}
                  {form && (
                    <Formulario
                      key={form.id || "nuevo"}
                      initial={form}
                      options={o}
                      residuos={optionsData.residuos}
                      tratamientos={optionsData.tratamientos}
                      onCancel={() => {
                        setForm(null);
                        setRevisarFormulario(null);
                      }}
                      onSave={save}
                      busy={busy}
                      validar={revisarFormulario === form}
                      declaracionPresentacion={
                        optionsData.manifiestoConfiguracionVigente?.declaracion
                      }
                      anexosExistentes={form.id && form.id === m?.id ? m.archivos.length : 0}
                    />
                  )}
                  {!form &&
                    id &&
                    (loadingDetail ? (
                      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240, width: "100%" }}>
                        <CircularProgress aria-label="Cargando manifiesto" />
                      </Stack>
                    ) : (
                      m && (
                        <>
                          <Card
                            sx={{
                              "& .MuiTypography-root": { color: "dark.main" },
                              "& .MuiTypography-subtitle1": { fontWeight: 700, fontSize: "1rem" },
                              "& .MuiButton-root": {
                                minHeight: { xs: 44, sm: 34 },
                                minWidth: "auto",
                                px: 1.5,
                                py: 0.75,
                                textTransform: "none",
                                fontSize: "0.8125rem",
                                "&.Mui-focusVisible": {
                                  outline: "3px solid",
                                  outlineColor: "primary.main",
                                  outlineOffset: 3,
                                },
                              },
                              "& input[type=file]": {
                                color: "dark.main",
                                fontSize: "0.875rem",
                                maxWidth: "100%",
                                width: "fit-content",
                                alignSelf: "flex-end",
                              },
                              "& input[type=file]::file-selector-button": {
                                minHeight: 44,
                                px: 2,
                                mr: 1.5,
                                font: "inherit",
                                fontWeight: 500,
                                color: "primary.main",
                                bgcolor: "background.paper",
                                border: 1,
                                borderColor: "primary.main",
                                borderRadius: 2,
                                cursor: "pointer",
                              },
                            }}
                          >
                            <CardContent>
                              <Stack spacing={2}>
                                <SoftButton
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<ArrowBackIcon />}
                                  sx={{ alignSelf: "flex-end" }}
                                  onClick={() => setId(null)}
                                >
                                  Volver al listado
                                </SoftButton>
                                <Typography variant="h5">
                                  {m.numero || "-"} · {m.estado}
                                </Typography>
                                {m.estado === "Pendiente de aprobación" && (
                                  <Typography variant="body2">
                                    Aprobaciones: transportista{" "}
                                    {m.intervenciones?.some(
                                      (i) => i.accion === "ACEPTAR" && i.rol === "Transportista"
                                    )
                                      ? "aprobado"
                                      : "pendiente"}{" "}
                                    · operador{" "}
                                    {m.intervenciones?.some(
                                      (i) => i.accion === "ACEPTAR" && i.rol === "Operador"
                                    )
                                      ? "aprobado"
                                      : "pendiente"}
                                  </Typography>
                                )}
                                <Typography>
                                  {["Generador", "Transportista", "Operador"]
                                    .map(
                                      (r) => m.snapshot?.[r]?.razonSocial || company(m[`id${r}`])
                                    )
                                    .join(" → ")}
                                </Typography>
                                <SoftButton
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<InfoOutlinedIcon />}
                                  sx={{ alignSelf: "flex-end" }}
                                  onClick={() => setInfoOpen(true)}
                                >
                                  Ver datos
                                </SoftButton>
                                <Dialog
                                  open={infoOpen}
                                  onClose={() => setInfoOpen(false)}
                                  fullWidth
                                  maxWidth="md"
                                  aria-labelledby="manifiesto-info-title"
                                  sx={{
                                    "& .MuiTypography-root": { color: "dark.main" },
                                    "& .MuiTypography-subtitle1": { fontWeight: 700 },
                                  }}
                                >
                                  <DialogTitle id="manifiesto-info-title">
                                    Datos del formulario y constancias
                                  </DialogTitle>
                                  <DialogContent dividers>
                                    <Stack spacing={2}>
                                      <Grid container spacing={2}>
                                        {["Generador", "Transportista", "Operador"].map((r) => {
                                          const p = m.snapshot?.[r];
                                          return (
                                            <Grid key={r} item xs={12} md={4}>
                                              <Typography variant="subtitle1">{r}</Typography>
                                              <Typography variant="body2">
                                                {p?.razonSocial || company(m[`id${r}`])}
                                              </Typography>
                                              {p && (
                                                <Typography variant="body2">
                                                  {p.domicilio}
                                                  <br />
                                                  CUIT: {p.cuit}
                                                  <br />
                                                  Legajo: {p.legajo}
                                                  <br />
                                                  C.A.A.: {p.caa} · Vto. {p.vencimiento}
                                                </Typography>
                                              )}
                                            </Grid>
                                          );
                                        })}
                                      </Grid>
                                      {vehiculo && (
                                        <>
                                          <Typography variant="subtitle1">
                                            Vehículo declarado
                                          </Typography>
                                          <Typography variant="body2">
                                            {vehiculo.tipo} · dominio {vehiculo.dominio} ·
                                            habilitación {vehiculo.habilitacion} · vence{" "}
                                            {vehiculo.vencimientoHabilitacion} · póliza{" "}
                                            {vehiculo.poliza} · seguro hasta{" "}
                                            {vehiculo.vencimientoSeguro}
                                          </Typography>
                                        </>
                                      )}
                                      <Typography variant="subtitle1">Residuos</Typography>
                                      {m.datos.residuos?.map((r, n) => (
                                        <Typography key={n} variant="body2">
                                          {r.descripcion} · {r.cantidad} {r.unidad} ·{" "}
                                          {r.estadoFisico}
                                          {r.contenedores && ` · Contenedores: ${r.contenedores}`}
                                        </Typography>
                                      ))}
                                      {[
                                        ["Componentes peligrosos", "componentes"],
                                        ["Toxicidad por inhalación", "inhalacion"],
                                        ["Toxicidad dérmica", "dermica"],
                                        ["Toxicidad oral", "oral"],
                                        ["Inflamabilidad", "inflamabilidad"],
                                        ["Toxicidad", "toxicidad"],
                                        ["Reactividad", "reactividad"],
                                        ["Instrucciones especiales", "instruccionesEspeciales"],
                                        ["Instrucciones al operador", "instruccionesOperador"],
                                        ["Teléfono operador", "telefonoOperador"],
                                        ["Teléfono transportista", "telefonoTransportista"],
                                      ].map(([label, k]) => (
                                        <Typography
                                          variant="body2"
                                          key={k}
                                          sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                                        >
                                          <strong>{label}:</strong>{" "}
                                          {typeof m.datos[k] === "boolean"
                                            ? m.datos[k]
                                              ? "Sí"
                                              : "No"
                                            : m.datos[k]}
                                        </Typography>
                                      ))}
                                      <Typography variant="body2">
                                        Tratamiento requerido:{" "}
                                        {m.snapshot?.tratamiento?.descripcion ||
                                          optionsData.tratamientos.find(
                                            (t) => String(t.id) === String(m.datos.idTratamiento)
                                          )?.descripcion}
                                      </Typography>
                                      {m.intervenciones?.map((i, n) => (
                                        <Card variant="outlined" key={n}>
                                          <CardContent>
                                            <Typography variant="body2">
                                              {LABELS[i.accion] || i.accion} · {i.rol} · {i.usuario}{" "}
                                              · Legajo:{" "}
                                              {i.legajo || "-"} · {i.fecha}
                                            </Typography>
                                            {i.motivo && (
                                              <Typography variant="body2">
                                                Motivo: {i.motivo}
                                              </Typography>
                                            )}
                                            {i.vehiculo && (
                                              <Typography variant="body2">
                                                Vehículo: {i.vehiculo.tipo} · {i.vehiculo.dominio} ·
                                                Póliza: {i.vehiculo.poliza}
                                              </Typography>
                                            )}
                                            {i.tratamiento && (
                                              <Typography variant="body2">
                                                Tratamiento realizado: {i.tratamiento.descripcion} ·{" "}
                                                {i.observaciones}
                                              </Typography>
                                            )}
                                          </CardContent>
                                        </Card>
                                      ))}
                                    </Stack>
                                  </DialogContent>
                                  <DialogActions>
                                    <SoftButton
                                      variant="outlined"
                                      color="primary"
                                      startIcon={<CloseIcon />}
                                      onClick={() => setInfoOpen(false)}
                                    >
                                      Cerrar
                                    </SoftButton>
                                  </DialogActions>
                                </Dialog>
                                {m.idAnterior && (
                                  <SoftButton
                                    variant="outlined"
                                    color="primary"
                                    sx={{ alignSelf: "flex-end" }}
                                    onClick={() => setId(m.idAnterior)}
                                  >
                                    Ver manifiesto anterior
                                  </SoftButton>
                                )}
                                <Stack
                                  direction="row"
                                  justifyContent="flex-end"
                                  flexWrap="wrap"
                                  useFlexGap
                                  spacing={1}
                                >
                                  <HistorialCambios modulo="manifiestos" id={m.id} nombre={m.numero || `Borrador · ${company(m.idGenerador)}`} />
                                  {canEdit && (
                                    <SoftButton
                                      variant="contained"
                                      color="primary"
                                      startIcon={<EditIcon />}
                                      onClick={() =>
                                        setForm({
                                          id: m.id,
                                          idGenerador: m.idGenerador,
                                          idTransportista: m.idTransportista,
                                          idOperador: m.idOperador,
                                          idAnterior: m.idAnterior,
                                          datos: m.datos,
                                        })
                                      }
                                    >
                                      Editar borrador
                                    </SoftButton>
                                  )}
                                  <SoftButton
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() =>
                                      run(async () => {
                                        if (preview) URL.revokeObjectURL(preview);
                                        setPreview(
                                          URL.createObjectURL(previewPDF().output("blob"))
                                        );
                                      })
                                    }
                                  >
                                    Vista previa
                                  </SoftButton>
                                  <SoftButton
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<DownloadIcon />}
                                    onClick={() =>
                                      run(async () =>
                                        previewPDF().save(`manifiesto-${m.numero || m.id}.pdf`)
                                      )
                                    }
                                  >
                                    Descargar formulario
                                  </SoftButton>
                                  <SoftButton
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<DownloadIcon />}
                                    onClick={() =>
                                      run(() =>
                                        download(
                                          "resolucion",
                                          m.idConfiguracion ||
                                            optionsData.manifiestoConfiguracionVigente.id,
                                          "resolucion.pdf"
                                        )
                                      )
                                    }
                                  >
                                    Descargar resolución
                                  </SoftButton>
                                  {m.estado === "Anulado" &&
                                    o.responsable &&
                                    o.empresas.some(
                                      (e) => e.propia && String(e.id) === String(m.idGenerador)
                                    ) && (
                                      <SoftButton
                                        variant="contained"
                                        color="primary"
                                        onClick={() =>
                                          setForm({
                                            idGenerador: m.idGenerador,
                                            idTransportista: m.idTransportista,
                                            idOperador: m.idOperador,
                                            idAnterior: m.id,
                                            datos: m.datos,
                                          })
                                        }
                                      >
                                        Crear reemplazo
                                      </SoftButton>
                                    )}
                                </Stack>
                                <Typography variant="subtitle1">Acciones disponibles</Typography>
                                <Stack
                                  direction="row"
                                  justifyContent="flex-end"
                                  flexWrap="wrap"
                                  useFlexGap
                                  spacing={1}
                                >
                                  {m.acciones?.map((a) => (
                                    <SoftButton
                                      disabled={busy}
                                      key={`${a.accion}-${a.rol}`}
                                      color={
                                        ["ANULAR", "RECHAZAR"].includes(a.accion)
                                          ? "error"
                                          : "primary"
                                      }
                                      variant={
                                        ["ANULAR", "RECHAZAR"].includes(a.accion)
                                          ? "outlined"
                                          : "contained"
                                      }
                                      sx={
                                        ["ANULAR", "RECHAZAR"].includes(a.accion)
                                          ? { "&&": { color: "error.main" } }
                                          : {}
                                      }
                                      onClick={() => {
                                        if (
                                          a.accion === "PRESENTAR" &&
                                          camposPendientes(m).some((e) => e.length)
                                        ) {
                                          const borrador = {
                                            id: m.id,
                                            idGenerador: m.idGenerador,
                                            idTransportista: m.idTransportista,
                                            idOperador: m.idOperador,
                                            idAnterior: m.idAnterior,
                                            datos: m.datos,
                                          };
                                          setRevisarFormulario(borrador);
                                          setForm(borrador);
                                          return;
                                        }
                                        setInput({});
                                        setActionError("");
                                        setAction(a);
                                      }}
                                    >
                                      {actionText(a, m)}
                                    </SoftButton>
                                  ))}
                                </Stack>
                                <Typography variant="subtitle1">Documentación anexa</Typography>
                                {!m.archivos.length && (
                                  <Typography variant="body2">
                                    No hay documentos adjuntos.
                                  </Typography>
                                )}
                                {m.archivos.map((f) => (
                                  <Stack
                                    key={f.id}
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={2}
                                    alignItems={{ xs: "flex-end", md: "center" }}
                                    sx={{
                                      p: 2,
                                      border: 1,
                                      borderColor: "grey.300",
                                      borderRadius: 2,
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={1.5}
                                      alignItems="center"
                                      sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        alignSelf: { xs: "stretch", md: "auto" },
                                      }}
                                    >
                                      <DescriptionIcon sx={{ color: "primary.main" }} />
                                      <Stack sx={{ minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 500, overflowWrap: "anywhere" }}
                                        >
                                          {f.nombre}
                                        </Typography>
                                        <Typography variant="caption">{f.tipo} · PDF</Typography>
                                      </Stack>
                                    </Stack>
                                    <SoftButton
                                      variant="text"
                                      color="primary"
                                      startIcon={<DownloadIcon />}
                                      disabled={busy}
                                      aria-label={`Descargar ${f.nombre}`}
                                      onClick={() => run(() => download("anexo", f.id, f.nombre))}
                                    >
                                      Descargar
                                    </SoftButton>
                                    {canEdit && (
                                      <SoftButton
                                        variant="text"
                                        color="error"
                                        sx={{ "&&": { color: "error.main" } }}
                                        startIcon={<CloseIcon />}
                                        disabled={busy}
                                        aria-label={`Quitar ${f.nombre}`}
                                        onClick={() =>
                                          run(() =>
                                            client.mutate({
                                              mutation: gql`
                                                mutation ($id: ID!) {
                                                  quitarAdjuntoManifiesto(id: $id)
                                                }
                                              `,
                                              variables: { id: f.id },
                                            })
                                          )
                                        }
                                      >
                                        Quitar
                                      </SoftButton>
                                    )}
                                  </Stack>
                                ))}
                                {canEdit && (
                                  <>
                                    <Campo
                                      label="Tipo de documentación"
                                      value={uploadType}
                                      onChange={setUploadType}
                                      options={["Contingencia", "Otro"].map((s) => ({
                                        id: s,
                                        label: s,
                                      }))}
                                    />
                                    <input
                                      type="file"
                                      accept="application/pdf"
                                      aria-label="Adjuntar documentación PDF"
                                      disabled={busy}
                                      onChange={(e) => {
                                        const archivo = e.target.files[0];
                                        e.target.value = "";
                                        if (archivo)
                                          run(() =>
                                            client.mutate({
                                              mutation: gql`
                                                mutation (
                                                  $id: ID!
                                                  $tipo: String!
                                                  $archivo: Upload!
                                                ) {
                                                  adjuntarManifiesto(
                                                    id: $id
                                                    tipo: $tipo
                                                    archivo: $archivo
                                                  ) {
                                                    id
                                                  }
                                                }
                                              `,
                                              variables: { id: m.id, tipo: uploadType, archivo },
                                            })
                                          );
                                      }}
                                    />
                                  </>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        </>
                      )
                    ))}
                </>
              )}
              {tab === 1 && (
                <Card>
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="h6">Resumen anual para la DDJJ</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Campo
                            label="Empresa"
                            autocomplete
                            value={summaryArgs.idEmpresa}
                            onChange={(v) => setSummaryArgs({ ...summaryArgs, idEmpresa: v })}
                            options={o.empresas
                              .filter((e) => o.autoridad || e.propia)
                              .map((e) => ({ id: e.id, label: e.razonSocial }))}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Campo
                            label="Actividad"
                            value={summaryArgs.actividad}
                            onChange={(v) => setSummaryArgs({ ...summaryArgs, actividad: v })}
                            options={["Generador", "Transportista", "Operador"].map((s) => ({
                              id: s,
                              label: s,
                            }))}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Campo
                            label="Año"
                            type="number"
                            value={summaryArgs.anio}
                            onChange={(v) => setSummaryArgs({ ...summaryArgs, anio: Number(v) })}
                          />
                        </Grid>
                      </Grid>
                      <SoftButton
                        variant="gradient"
                        color="primary"
                        startIcon={<SearchIcon />}
                        sx={{ alignSelf: "flex-end", width: { xs: "100%", sm: "fit-content" }, textTransform: "none" }}
                        disabled={busy || !summaryArgs.idEmpresa}
                        onClick={() =>
                          run(async () => {
                            const r = await client.query({
                              query: gql`
                                query ($idEmpresa: ID!, $actividad: String!, $anio: Int!) {
                                  resumenManifiestos(
                                    idEmpresa: $idEmpresa
                                    actividad: $actividad
                                    anio: $anio
                                  )
                                }
                              `,
                              variables: summaryArgs,
                              fetchPolicy: "network-only",
                            });
                            setSummary(r.data.resumenManifiestos);
                          })
                        }
                      >
                        Consultar resumen
                      </SoftButton>
                      {summary?.grupos.map((g, n) => (
                        <Card variant="outlined" key={n}>
                          <CardContent>
                            <Typography>
                              {g.residuo}: {g.cantidad} {g.unidad} · {g.tratamiento?.descripcion} ·{" "}
                              {g.manifiestos.length} manifiestos
                            </Typography>
                            {g.manifiestos.map((x) => (
                              <Button
                                key={x.id}
                                onClick={() => {
                                  setTab(0);
                                  setId(String(x.id));
                                }}
                              >
                                {x.numero}
                              </Button>
                            ))}
                          </CardContent>
                        </Card>
                      ))}
                      {summary && !summary.grupos.length && (
                        <Typography>Sin movimientos finalizados para este período.</Typography>
                      )}
                      {summary?.excluidos.map((x) => (
                        <Typography key={x.id} variant="body2">
                          Excluido:{" "}
                          <Button
                            onClick={() => {
                              setTab(0);
                              setId(String(x.id));
                            }}
                          >
                            {x.numero || "-"}
                          </Button>{" "}
                          · {x.estado}
                        </Typography>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              )}
              {tab === 2 && (!o.autoridad || o.puedeInscripciones || o.puedeHabilitaciones) && (
                <DatosEmpresa options={o} onRefresh={refreshOptions} report={handleSnackbar} />
              )}
              {tab === 3 && o.admin && (
                <Administracion
                  config={optionsData.manifiestoConfiguracionVigente}
                  onRefresh={refreshOptions}
                  report={handleSnackbar}
                />
              )}
            </>
          )
        )}
        <Dialog open={Boolean(action)} onClose={() => !busy && setAction(null)} fullWidth>
          <DialogTitle>{action && actionText(action, m)}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {actionError && <Alert severity="error">{actionError}</Alert>}
              {action?.accion === "PRESENTAR" && (
                <Alert severity="info">
                  Al presentar se fija el contenido, se asigna el número y se solicita la aprobación
                  de la transportista.
                </Alert>
              )}
              <Typography variant="body2">
                ¿Confirmás esta acción sobre el manifiesto {m?.numero || "en borrador"}?
              </Typography>
              {["ANULAR", "RECHAZAR"].includes(action?.accion) && (
                <Campo
                  required
                  label="Motivo obligatorio"
                  value={input.motivo}
                  onChange={(v) => setInput({ ...input, motivo: v })}
                  multiline
                />
              )}
              {action?.accion === "TRASLADO" && (
                <Typography variant="body2">
                  Vehículo declarado: {m?.snapshot?.vehiculo?.tipo} ·{" "}
                  {m?.snapshot?.vehiculo?.dominio} · póliza {m?.snapshot?.vehiculo?.poliza}
                </Typography>
              )}
              {action?.accion === "DISPOSICION" && (
                <>
                  <Campo
                    required
                    label="Tratamiento realizado"
                    value={input.idTratamiento}
                    onChange={(v) => setInput({ ...input, idTratamiento: v })}
                    options={optionsData?.tratamientos.map((t) => ({
                      id: t.id,
                      label: t.descripcion,
                    }))}
                  />
                  <Campo
                    label="Observaciones de disposición"
                    value={input.observaciones}
                    onChange={(v) => setInput({ ...input, observaciones: v })}
                    multiline
                  />
                </>
              )}
              {action?.accion === "PRESENTAR" && (
                <Alert severity="info">{optionsData?.manifiestoConfiguracionVigente?.declaracion}</Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              gap: 1,
              "& .MuiButton-root": { minHeight: 44, textTransform: "none" },
            }}
          >
            <SoftButton
              variant="outlined"
              color="primary"
              disabled={busy}
              onClick={() => setAction(null)}
            >
              Cancelar
            </SoftButton>
            <SoftButton
              variant="gradient"
              color={["ANULAR", "RECHAZAR"].includes(action?.accion) ? "error" : "primary"}
              disabled={
                busy ||
                (["ANULAR", "RECHAZAR"].includes(action?.accion) && !input.motivo?.trim()) ||
                (action?.accion === "DISPOSICION" && !input.idTratamiento)
              }
              onClick={() =>
                run(async () => {
                  try {
                    await client.mutate({
                      mutation: actionMutation(action.accion),
                      variables: { id: m.id, actividad: action.rol, input },
                    });
                  } catch (e) {
                    setActionError(errorMessage(e));
                    throw e;
                  }
                  setAction(null);
                  handleSnackbar("Intervención registrada", "success");
                })
              }
            >
              {busy ? "Procesando…" : "Confirmar"}
            </SoftButton>
          </DialogActions>
        </Dialog>
        <Dialog
          open={Boolean(preview)}
          onClose={() => {
            URL.revokeObjectURL(preview);
            setPreview(null);
          }}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>Vista previa del manifiesto</DialogTitle>
          <DialogContent>
            <iframe
              title="Formulario de manifiesto"
              src={preview || ""}
              style={{ width: "100%", height: "70vh", border: 0 }}
            />
          </DialogContent>
        </Dialog>
      </Stack>
    </DashboardLayout>
  );
}
